'use strict'

const result = require('folktale/result')
const { union } = require('folktale/adt/union')
const pull_ = require('lodash/pull')
const merge_ = require('lodash/merge')
const uniqueId_ = require('lodash/uniqueId')
const isFunction_ = require('lodash/isFunction')
const isObject_ = require('lodash/isObject')

const LogEvent = union('LogEvent', {
  BI: ({ biEvent }) => ({ biEvent }),
  Breadcrumb: ({ breadcrumb }) => ({ breadcrumb }),
  TraceStart: ({ traceId, actionName, startOptions = {} }) => ({
    traceId,
    actionName,
    startOptions
  }),
  TraceEnd: ({
    traceId,
    actionName,
    startOptions,
    endOptions = {},
    durationMs
  }) => ({
    traceId,
    actionName,
    startOptions,
    endOptions,
    durationMs
  }),
  Info: ({ message, options, sessionData }) => ({
    message,
    options,
    sessionData
  }),
  Warn: ({ message, options, sessionData }) => ({
    message,
    options,
    sessionData
  }),
  Error: ({ error, options, sessionData }) => ({
    error,
    options,
    sessionData
  })
})

const createRegistrar = () => {
  const callbacks = []

  const register = cb => {
    callbacks.push(cb)
    return () => {
      pull_(callbacks, cb)
    }
  }
  const getCallbacks = () => callbacks.slice()

  return { register, getCallbacks }
}

const validateArgument = (argName, argValue) => {
  if (argValue == null) {
    throw new Error(`Logger: the argument ${argName} is required`)
  }
}

const validateHandlerCreators = handlerCreators => {
  if (
    !handlerCreators ||
    !Array.isArray(handlerCreators) ||
    handlerCreators.length === 0
  ) {
    return result.Error(
      '`handlerCreators` is missing or empty, the logger needs at least one handler to work.'
    )
  }

  return handlerCreators.reduce((acc, creator) => {
    return acc.chain(() => {
      return isFunction_(creator)
        ? acc
        : result.Error('`handlerCreators` must be an array of functions.')
    })
  }, result.Ok(handlerCreators))
}

const validateHandlers = handlers => {
  return handlers.reduce((acc, handler) => {
    return acc.chain(() => {
      if (!isObject_(handler)) {
        return result.Error('Handler must be an object.')
      }

      if (!isFunction_(handler.init)) {
        return result.Error('Handler must have an init function.')
      }

      if (!isFunction_(handler.log)) {
        return result.Error('Handler must have a log function.')
      }

      return acc
    })
  }, result.Ok(handlers))
}

const createHandlers = handlerCreators => {
  return validateHandlerCreators(handlerCreators)
    .map(validatedHandlerCreators =>
      validatedHandlerCreators.map(creator => creator())
    )
    .chain(handlers => validateHandlers(handlers))
    .fold(errorMessage => {
      throw new Error(errorMessage)
    }, validatedHandlers => validatedHandlers)
}

const reportBI = handlers => biEvent => {
  const logEvent = LogEvent.BI({ biEvent })
  handlers.forEach(handler => handler.log(logEvent))
}

const reportBreadcrumb = handlers => breadcrumb => {
  const logEvent = LogEvent.Breadcrumb({ breadcrumb })
  handlers.forEach(handler => handler.log(logEvent))
}

const reportInfo = (handlers, getSessionData) => (message, options) => {
  const logEvent = LogEvent.Info({
    message,
    options,
    sessionData: getSessionData()
  })
  handlers.forEach(handler => handler.log(logEvent))
}

const reportWarn = (handlers, getSessionData) => (message, options) => {
  const logEvent = LogEvent.Warn({
    message,
    options,
    sessionData: getSessionData()
  })
  handlers.forEach(handler => handler.log(logEvent))
}

const reportError = (handlers, getSessionData) => (error, options) => {
  const logEvent = LogEvent.Error({
    error,
    options,
    sessionData: getSessionData()
  })
  handlers.forEach(handler => handler.log(logEvent))
}

const traceStart = (handlers, tracesMap) => ({
  actionName,
  options: startOptions
} = {}) => {
  validateArgument('actionName', actionName)

  const ts = Date.now()
  const traceId = uniqueId_()
  tracesMap.set(traceId, { actionName, startOptions, ts })

  handlers.forEach(handler =>
    handler.log(
      LogEvent.TraceStart({
        traceId,
        actionName,
        startOptions
      })
    )
  )

  return traceId
}

const traceEnd = (handlers, tracesMap) => ({
  traceId,
  options: endOptions
} = {}) => {
  validateArgument('traceId', traceId)
  const trace = tracesMap.get(traceId)
  if (trace == null) {
    throw new Error(
      `Logger: could not find a trace start for traceId ${traceId}`
    )
  }
  tracesMap.delete(traceId)

  const { actionName, startOptions, ts } = trace
  const durationMs = Date.now() - ts

  handlers.forEach(handler =>
    handler.log(
      LogEvent.TraceEnd({
        traceId,
        actionName,
        startOptions,
        endOptions,
        durationMs
      })
    )
  )
}

const traceAsync = (handlers, tracesMap) => (
  traceStartArg,
  errorToOptions,
  fn
) => {
  const traceId = traceStart(handlers, tracesMap)(traceStartArg)

  const promise = fn()
  promise
    .then(() => traceEnd(handlers, tracesMap)({ traceId }))
    .catch(e =>
      traceEnd(handlers, tracesMap)({ traceId, options: errorToOptions(e) })
    )
  return promise
}

const createLogger = ({ handlerCreators } = {}) => {
  const handlers = createHandlers(handlerCreators)
  const sessionDataRegistrar = createRegistrar()
  const tracesMap = new Map()

  const getSessionData = () =>
    sessionDataRegistrar
      .getCallbacks()
      .reduce((acc, cb) => merge_(acc, cb()), {})

  const callInit = initParams => {
    const initParamsWithLogger = Object.assign({ logger }, initParams)
    handlers.forEach(handler => handler.init(initParamsWithLogger))
  }

  const logger = {
    addSessionData: sessionDataRegistrar.register,
    init: callInit,
    bi: reportBI(handlers),
    breadcrumb: reportBreadcrumb(handlers),
    info: reportInfo(handlers, getSessionData),
    warn: reportWarn(handlers, getSessionData),
    error: reportError(handlers, getSessionData),
    traceStart: traceStart(handlers, tracesMap),
    traceEnd: traceEnd(handlers, tracesMap),
    traceAsync: traceAsync(handlers, tracesMap)
  }

  return logger
}

module.exports = {
  create: createLogger,
  matchAny: union.any
}
